import { useCallback, useEffect, useMemo, useState } from 'react';
import { OrganizationPicker } from '../../components/Pickers';
import { useAuth } from '../../context/AuthContext';
import { useAppResource } from '../../context/AppDataContext';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import { organizationService } from '../../services/organizationService';
import { outletService } from '../../services/outletService';
import BulkUserImport from '../../components/BulkUserImport';
import { canAssignRole, canManageUser } from '../../utils/permissions';
import '../Users/Users.css';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import './Clients.css';
import { PageLoader } from '../../components/Loader';

const emptyForm = {
  email: '',
  name: '',
  client_title: '',
  phone: '',
  client_organization_id: '',
  client_access_level: 'organization',
  client_outlet_ids: [],
};

/** Filter option for client logins not linked to a client yet. */
const NOT_LINKED = [{ value: 'none', label: 'Not linked', sub: 'no client chosen' }];

const TITLE_SUGGESTIONS = ['Owner', 'Head Office', 'Regional Manager', 'Area Manager', 'Store Manager'];

const BULK_COLUMNS = [
  { key: 'name', label: 'Name', aliases: ['full name'] },
  { key: 'email', label: 'Email', required: true, aliases: ['email id', 'e mail', 'mail'] },
  { key: 'title', label: 'Title', aliases: ['designation', 'position'], hint: 'e.g. Area Manager, Store Manager' },
  { key: 'phone', label: 'Phone', aliases: ['mobile', 'phone number', 'contact'] },
  { key: 'organization', label: 'Organization', required: true, aliases: ['org', 'brand', 'company'], hint: 'organization name, exactly as on the Organizations page' },
  {
    key: 'outlets',
    label: 'Outlet codes',
    aliases: ['outlets', 'outlet', 'outlet code', 'store code', 'store codes', 'stores'],
    hint: 'leave blank for the whole organization; otherwise outlet codes separated by ; (one for a store manager, several for an area manager)',
  },
];

const BULK_SAMPLE = [
  { name: 'Neha Shah', email: 'neha@brand.com', title: 'Head Office', phone: '', organization: 'Your Organization', outlets: '' },
  { name: 'Arjun Rao', email: 'arjun@brand.com', title: 'Area Manager', phone: '', organization: 'Your Organization', outlets: 'BLR01;BLR02' },
  { name: 'Meera Iyer', email: 'meera@brand.com', title: 'Store Manager', phone: '', organization: 'Your Organization', outlets: 'BLR01' },
];

function outletLabel(outlet) {
  const code = outlet.outlet_code ? ` (${outlet.outlet_code})` : '';
  const city = outlet.city ? ` · ${outlet.city}` : '';
  return `${outlet.name}${code}${city}`;
}

function norm(value) {
  return String(value ?? '').trim().toLowerCase();
}

/** Describes what a client can see, for the table. */
function AccessSummary({ client }) {
  if (!client.client_organization_id) {
    return (
      <span className="client-access client-access--none" title="Sees only schedules shared with them on the schedule form">
        Not linked
      </span>
    );
  }
  if (client.client_access_level === 'outlet') {
    const outlets = client.client_outlets || [];
    return (
      <div className="client-access-cell">
        <span className="client-access client-access--outlet">
          {outlets.length === 1 ? '1 outlet' : `${outlets.length} outlets`}
        </span>
        <small>{outlets.map((outlet) => outlet.outlet_code || outlet.name).join(', ')}</small>
      </div>
    );
  }
  return <span className="client-access client-access--org">All outlets</span>;
}

/**
 * Client users: people from the audited organization who can view audits.
 * Organization-level clients see every audit of the organization's outlets;
 * outlet-level clients (store / area managers) see only their outlets.
 */
/**
 * Client logins (portal users). Embedded = the "Client logins" tab of
 * Clients & Stores; prefillOrgId opens the add form for that client.
 */
function Clients({ embedded = false, prefillOrgId = '', onPrefillUsed }) {
  const { user: currentUser } = useAuth();
  const loadUsers = useCallback(() => userService.getAll(), []);
  const loadRoles = useCallback(() => roleService.getAll(), []);
  const loadOrganizations = useCallback(() => organizationService.getAll(), []);
  const loadOutlets = useCallback(() => outletService.getAll(), []);
  const { data: users = [], loading: usersLoading, error: usersError, refresh: refreshUsers } =
    useAppResource('users', loadUsers, []);
  const { data: roles = [] } = useAppResource('roles', loadRoles, []);
  const { data: organizations = [] } = useAppResource('organizations', loadOrganizations, []);
  const { data: outlets = [] } = useAppResource('outlets', loadOutlets, []);

  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [outletSearch, setOutletSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('');

  const clientRole = useMemo(() => roles.find((role) => role.name === 'client'), [roles]);
  const canCreate = Boolean(clientRole) && canAssignRole(currentUser, 'client');

  const clients = useMemo(
    () =>
      users
        .filter((user) => user.role_name === 'client')
        .sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email))),
    [users]
  );

  // Organization filter; the grid's search box does the text search.
  const visibleClients = useMemo(
    () =>
      clients.filter((client) => {
        if (orgFilter === 'none' && client.client_organization_id) return false;
        if (orgFilter && orgFilter !== 'none' && String(client.client_organization_id) !== orgFilter) return false;
        return true;
      }),
    [clients, orgFilter],
  );

  const unlinkedCount = clients.filter((client) => !client.client_organization_id).length;

  const formOutlets = useMemo(
    () =>
      outlets
        .filter((outlet) => String(outlet.organization_id) === String(form.client_organization_id))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [outlets, form.client_organization_id]
  );

  const shownFormOutlets = useMemo(() => {
    const term = norm(outletSearch);
    if (!term) return formOutlets;
    return formOutlets.filter((outlet) => norm(outletLabel(outlet)).includes(term));
  }, [formOutlets, outletSearch]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      client_organization_id: organizations.length === 1 ? String(organizations[0].id) : '',
    });
    setOutletSearch('');
    setError('');
    setFormOpen(true);
  };

  // "Add a client login" on the Clients tab: open the form for that client.
  useEffect(() => {
    if (!prefillOrgId || !canCreate || !organizations.length) return;
    setEditing(null);
    setForm({ ...emptyForm, client_organization_id: String(prefillOrgId) });
    setOutletSearch('');
    setError('');
    setFormOpen(true);
    onPrefillUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillOrgId, canCreate, organizations.length]);

  const openEdit = (client) => {
    setEditing(client);
    setForm({
      email: client.email || '',
      name: client.name || '',
      client_title: client.client_title || '',
      phone: client.phone || '',
      client_organization_id: client.client_organization_id ? String(client.client_organization_id) : '',
      client_access_level: client.client_access_level || 'organization',
      client_outlet_ids: (client.client_outlets || []).map((outlet) => String(outlet.id)),
    });
    setOutletSearch('');
    setError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const change = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Outlets belong to one organization: clear them when it changes.
      ...(name === 'client_organization_id' ? { client_outlet_ids: [] } : {}),
    }));
  };

  const toggleOutlet = (id) =>
    setForm((prev) => {
      const key = String(id);
      const has = prev.client_outlet_ids.includes(key);
      return {
        ...prev,
        client_outlet_ids: has
          ? prev.client_outlet_ids.filter((item) => item !== key)
          : [...prev.client_outlet_ids, key],
      };
    });

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.client_organization_id) {
      setError('Choose the organization this client belongs to.');
      return;
    }
    if (form.client_access_level === 'outlet' && form.client_outlet_ids.length === 0) {
      setError('Choose at least one outlet, or give access to the whole organization.');
      return;
    }
    const payload = {
      email: form.email.trim(),
      name: form.name.trim(),
      phone: form.phone.trim(),
      client_title: form.client_title.trim(),
      client_organization_id: Number(form.client_organization_id),
      client_access_level: form.client_access_level,
      client_outlet_ids:
        form.client_access_level === 'outlet' ? form.client_outlet_ids.map(Number) : [],
    };
    setSaving(true);
    try {
      if (editing) {
        await userService.update(editing.id, payload);
      } else {
        await userService.create({ ...payload, role_id: Number(clientRole.id), provider: 'google' });
      }
      closeForm();
      await refreshUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete client ${client.email}? They will no longer be able to view schedules.`)) {
      return;
    }
    setError('');
    try {
      await userService.delete(client.id);
      await refreshUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const toBulkPayload = useCallback(
    (record) => {
      const email = record.email.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { error: 'Missing or invalid email' };
      }
      const organization = organizations.find((org) => norm(org.name) === norm(record.organization));
      if (!organization) {
        return { error: `Organization "${record.organization}" not found` };
      }
      const codes = record.outlets
        .split(/[;|]/)
        .map((code) => code.trim())
        .filter(Boolean);
      const orgOutlets = outlets.filter((outlet) => String(outlet.organization_id) === String(organization.id));
      const outletIds = [];
      for (const code of codes) {
        const outlet = orgOutlets.find(
          (item) => norm(item.outlet_code) === norm(code) || norm(item.name) === norm(code)
        );
        if (!outlet) {
          return { error: `Outlet "${code}" is not an outlet of ${organization.name}` };
        }
        outletIds.push(Number(outlet.id));
      }
      return {
        payload: {
          email,
          name: record.name,
          phone: record.phone,
          client_title: record.title,
          role_id: Number(clientRole?.id),
          provider: 'google',
          client_organization_id: Number(organization.id),
          client_access_level: outletIds.length ? 'outlet' : 'organization',
          client_outlet_ids: [...new Set(outletIds)],
        },
      };
    },
    [organizations, outlets, clientRole]
  );

  const orgSelect = (
    <div className="clients-page__org-filter">
      <OrganizationPicker
        organizations={organizations}
        value={orgFilter}
        onChange={(value) => setOrgFilter(value)}
        emptyLabel="All clients"
        allowCreate={false}
        ariaLabel="Filter by client"
        extraOptions={unlinkedCount > 0 ? NOT_LINKED : []}
      />
    </div>
  );

  const accessText = (client) => {
    if (!client.client_organization_id) return 'Not linked';
    if (client.client_access_level === 'outlet') {
      return (client.client_outlets || [])
        .map((outlet) => `${outlet.name} ${outlet.outlet_code || ''}`.trim())
        .join(', ');
    }
    return 'All outlets';
  };

  const columns = [
    { headerName: 'Name', field: 'name', valueFormatter: (p) => dash(p.value), minWidth: 160 },
    { headerName: 'Email', field: 'email', minWidth: 220 },
    { headerName: 'Title', field: 'client_title', valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Organization',
      field: 'client_organization_name',
      valueFormatter: (p) => dash(p.value),
    },
    {
      headerName: 'Can view',
      colId: 'access',
      valueGetter: (p) => accessText(p.data),
      minWidth: 200,
      autoHeight: true,
      cellRenderer: (p) => <AccessSummary client={p.data} />,
    },
    actionsColumn((client) => {
      const canEdit = canManageUser(currentUser, client);
      return (
        <>
          <button
            type="button"
            className="users-table__btn users-table__btn--edit"
            onClick={() => openEdit(client)}
            disabled={!canEdit}
          >
            Edit
          </button>
          <button
            type="button"
            className="users-table__btn users-table__btn--delete"
            onClick={() => remove(client)}
            disabled={!canEdit}
          >
            Delete
          </button>
        </>
      );
    }, { className: 'users-table__actions' }),
  ];

  const visibleError = error || usersError;
  const selectedOrg = organizations.find((org) => String(org.id) === String(form.client_organization_id));

  return (
    <div className={embedded ? 'users-page clients-page clients-page--embedded' : 'page users-page clients-page'}>
      <header className={embedded ? 'hub-section__toolbar' : 'page__header users-page__header'}>
        <div>
          {embedded ? (
            <h2>Client logins</h2>
          ) : (
            <>
              <p className="page__eyebrow">Management</p>
              <h1>Clients</h1>
            </>
          )}
          <p className="users-page__subtitle">
            People from the audited organization who can follow its schedules (read-only). Head
            office sees every outlet; area and store managers see only their outlets.
          </p>
        </div>
        {canCreate && (
          <div className="users-page__actions">
            <button
              type="button"
              className="users-page__add-btn users-page__add-btn--ghost"
              onClick={() => setBulkOpen(true)}
            >
              Bulk add clients
            </button>
            <button type="button" className="users-page__add-btn" onClick={openCreate}>
              + Add Client
            </button>
          </div>
        )}
      </header>

      {visibleError && !formOpen && (
        <div className="users-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      {unlinkedCount > 0 && (
        <div className="clients-page__notice">
          {unlinkedCount} client{unlinkedCount === 1 ? ' is' : 's are'} not linked to an
          organization yet and only see{unlinkedCount === 1 ? 's' : ''} schedules shared with them one
          by one. Edit {unlinkedCount === 1 ? 'it' : 'them'} to choose an organization and outlets.
        </div>
      )}

      <section className="users-page__card">
        {usersLoading && clients.length === 0 ? (
          <PageLoader label="Loading clients…" />
        ) : clients.length === 0 ? (
          <p className="users-page__empty">
            No clients yet. Add the people from the organization who should be able to view its schedules.
          </p>
        ) : (
          <DataGrid
            rowData={visibleClients}
            columnDefs={columns}
            searchPlaceholder="Search name, email, title or outlet…"
            toolbar={orgSelect}
          />
        )}
      </section>

      {bulkOpen && (
        <BulkUserImport
          title="Bulk add clients"
          intro="Add many client users at once. Leave Outlet codes blank to give someone every outlet of the organization; list outlet codes for store and area managers."
          columns={BULK_COLUMNS}
          sampleRows={BULK_SAMPLE.map((row) => ({
            ...row,
            organization: organizations[0]?.name || row.organization,
            outlets: row.outlets
              ? outlets
                  .filter((outlet) => String(outlet.organization_id) === String(organizations[0]?.id))
                  .slice(0, row.outlets.split(';').length)
                  .map((outlet) => outlet.outlet_code || outlet.name)
                  .join(';') || row.outlets
              : '',
          }))}
          templateName="clients.csv"
          toPayload={toBulkPayload}
          onClose={() => setBulkOpen(false)}
          onDone={refreshUsers}
        />
      )}

      {formOpen && (
        <div className="users-modal">
          <div className="users-modal__backdrop" onClick={closeForm} />
          <div className="users-modal__panel clients-modal" role="dialog" aria-modal="true">
            <div className="users-modal__header">
              <h2>{editing ? 'Edit Client' : 'Add Client'}</h2>
              <button type="button" onClick={closeForm} aria-label="Close">
                ×
              </button>
            </div>

            <form className="users-form" onSubmit={submit}>
              {error && (
                <div className="users-page__alert" role="alert">
                  {error}
                </div>
              )}

              <label>
                Email
                <input type="email" name="email" value={form.email} onChange={change} required />
                <span className="users-form__hint">They sign in to the admin panel with this Google account.</span>
              </label>

              <label>
                Name
                <input type="text" name="name" value={form.name} onChange={change} />
              </label>

              <div className="clients-form__row">
                <label>
                  Title
                  <input
                    type="text"
                    name="client_title"
                    value={form.client_title}
                    onChange={change}
                    list="client-title-options"
                    placeholder="e.g. Area Manager"
                  />
                  <datalist id="client-title-options">
                    {TITLE_SUGGESTIONS.map((title) => (
                      <option key={title} value={title} />
                    ))}
                  </datalist>
                </label>
                <label>
                  Phone
                  <input type="tel" name="phone" value={form.phone} onChange={change} />
                </label>
              </div>

              <label>
                Organization
                <OrganizationPicker
                  organizations={organizations}
                  value={form.client_organization_id}
                  onChange={(value) => change({ target: { name: 'client_organization_id', value } })}
                  required
                />
              </label>

              <fieldset className="clients-form__access">
                <legend>What can they view?</legend>
                <label className="clients-form__option">
                  <input
                    type="radio"
                    name="client_access_level"
                    value="organization"
                    checked={form.client_access_level === 'organization'}
                    onChange={change}
                  />
                  <span>
                    <strong>Whole organization</strong>
                    <small>
                      Every schedule at every outlet of {selectedOrg ? selectedOrg.name : 'the organization'},
                      including outlets added later. For owners and head office.
                    </small>
                  </span>
                </label>
                <label className="clients-form__option">
                  <input
                    type="radio"
                    name="client_access_level"
                    value="outlet"
                    checked={form.client_access_level === 'outlet'}
                    onChange={change}
                  />
                  <span>
                    <strong>Specific outlets</strong>
                    <small>
                      Only schedules at the outlets ticked below. One outlet for a store manager,
                      several for an area manager.
                    </small>
                  </span>
                </label>

                {form.client_access_level === 'outlet' && (
                  <div className="clients-form__outlets">
                    {!form.client_organization_id ? (
                      <p className="users-form__hint">Choose the organization first.</p>
                    ) : formOutlets.length === 0 ? (
                      <p className="users-form__hint">This organization has no outlets yet.</p>
                    ) : (
                      <>
                        {formOutlets.length > 8 && (
                          <input
                            type="search"
                            placeholder="Search outlets…"
                            value={outletSearch}
                            onChange={(event) => setOutletSearch(event.target.value)}
                            aria-label="Search outlets"
                          />
                        )}
                        <div className="clients-form__outlet-list">
                          {shownFormOutlets.map((outlet) => (
                            <label key={outlet.id} className="clients-form__outlet">
                              <input
                                type="checkbox"
                                checked={form.client_outlet_ids.includes(String(outlet.id))}
                                onChange={() => toggleOutlet(outlet.id)}
                              />
                              <span>{outletLabel(outlet)}</span>
                            </label>
                          ))}
                        </div>
                        <p className="users-form__hint">
                          {form.client_outlet_ids.length} of {formOutlets.length} outlet
                          {formOutlets.length === 1 ? '' : 's'} selected
                        </p>
                      </>
                    )}
                  </div>
                )}
              </fieldset>

              <div className="users-form__actions">
                <button type="button" className="users-form__cancel" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="users-form__submit" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Client' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
